import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Users, GraduationCap, Search } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Admin-only section on the Profile page that shows TWO separate lists:
 *   - Users  (everyone except students — admin, canteen_admin, parent, staff…)
 *   - Students (role === "student")
 *
 * Each list has its own search box, scoped to the admin's school.
 */
export default function AdminUserStudentLists() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userQuery, setUserQuery] = useState("");
  const [studentQuery, setStudentQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/admin/users")
      .then(r => setAll(Array.isArray(r.data) ? r.data : []))
      .finally(() => setLoading(false));
  }, []);

  const users = useMemo(() => {
    const q = userQuery.toLowerCase();
    return all
      .filter(u => u.role !== "student")
      .filter(u => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.role || "").includes(q));
  }, [all, userQuery]);

  const students = useMemo(() => {
    const q = studentQuery.toLowerCase();
    return all
      .filter(u => u.role === "student")
      .filter(u => !q || (u.name || "").toLowerCase().includes(q) || (u.student_id || "").toLowerCase().includes(q) || (u.grade || "").toLowerCase().includes(q));
  }, [all, studentQuery]);

  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="admin-user-student-lists">
      {/* Users panel */}
      <div className="bg-white rounded-2xl shadow-soft border border-border overflow-hidden">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-cream-200 text-brand flex items-center justify-center">
              <Users size={16} />
            </div>
            <div>
              <h3 className="font-heading text-lg font-extrabold text-ink">Users</h3>
              <div className="text-[11px] text-ink-500">{users.length} non-student accounts</div>
            </div>
          </div>
          <Link to="/admin/users" data-testid="profile-manage-users"
            className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-ink text-white hover:bg-ink-700">
            Manage
          </Link>
        </div>
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
            <input data-testid="users-list-search" type="text" placeholder="Search users…"
              value={userQuery} onChange={e => setUserQuery(e.target.value)}
              className="block w-full rounded-xl border border-border pl-9 pr-3 py-2 text-sm focus:border-brand focus:outline-none" />
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {loading && <div className="p-6 text-center text-sm text-ink-500">Loading…</div>}
          {!loading && users.length === 0 && (
            <div className="p-6 text-center text-sm text-ink-500">No users found.</div>
          )}
          {!loading && users.map((u, i) => (
            <div key={u.id} className="px-5 py-3 border-b border-border last:border-b-0 flex items-center justify-between" data-testid={`profile-user-row-${i}`}>
              <div>
                <div className="font-semibold text-ink">{u.name}</div>
                <div className="text-[11px] text-ink-500">{u.email}</div>
              </div>
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-cream-200 text-ink-700 tracking-wider">
                {u.role?.replace("_", " ")}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Students panel */}
      <div className="bg-white rounded-2xl shadow-soft border border-border overflow-hidden">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <GraduationCap size={16} />
            </div>
            <div>
              <h3 className="font-heading text-lg font-extrabold text-ink">Students</h3>
              <div className="text-[11px] text-ink-500">{students.length} enrolled</div>
            </div>
          </div>
          <Link to="/admin/users" data-testid="profile-manage-students"
            className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800">
            Manage
          </Link>
        </div>
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
            <input data-testid="students-list-search" type="text" placeholder="Search students by name, ID or grade…"
              value={studentQuery} onChange={e => setStudentQuery(e.target.value)}
              className="block w-full rounded-xl border border-border pl-9 pr-3 py-2 text-sm focus:border-brand focus:outline-none" />
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {loading && <div className="p-6 text-center text-sm text-ink-500">Loading…</div>}
          {!loading && students.length === 0 && (
            <div className="p-6 text-center text-sm text-ink-500">No students found.</div>
          )}
          {!loading && students.map((s, i) => (
            <div key={s.id} className="px-5 py-3 border-b border-border last:border-b-0 flex items-center justify-between" data-testid={`profile-student-row-${i}`}>
              <div>
                <div className="font-semibold text-ink">{s.name}</div>
                <div className="text-[11px] text-ink-500">{s.student_id || "—"} · {s.grade || "—"}</div>
              </div>
              {s.rfid_card_id && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-cream-200 text-ink-700">{s.rfid_card_id}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
